/**
 * Decode the HTML entities we actually produce in content frontmatter.
 *
 * Frontmatter YAML is opaque to HTML — when an author writes
 * `title: "E&#8209;facturatie"` the string is stored verbatim. Vue's `{{ }}`
 * binding escapes the ampersand, so the literal `&#8209;` ends up on screen
 * instead of the non-breaking hyphen (U+2011) the author wanted.
 *
 * We don't want to open the `v-html` door, so we decode the few entities
 * commonly used in titles/labels on our way out of the content layer. This
 * keeps the sanitisation surface small: numeric (`&#NNN;`), hex
 * (`&#xHH;`), plus the four named entities that predictably turn up in
 * sentence text.
 */
const NAMED: Record<string, string> = {
  amp: "&",
  nbsp: "\u00a0",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  shy: "\u00ad",
};

export function decodeEntities(s: string | undefined | null): string {
  if (!s) return s ?? "";
  if (s.indexOf("&") < 0) return s;
  return s
    .replace(/&#(\d+);/g, (m, code) => {
      const n = Number(code);
      return Number.isFinite(n) ? String.fromCodePoint(n) : m;
    })
    .replace(/&#x([0-9a-fA-F]+);/g, (m, code) => {
      const n = parseInt(code, 16);
      return Number.isFinite(n) ? String.fromCodePoint(n) : m;
    })
    .replace(/&([a-zA-Z]+);/g, (m, name) => NAMED[name] ?? m);
}

/**
 * Recursively decode `title` and `description` fields on a nav-tree node
 * and all its descendants. Mutates in place and returns the same reference.
 */
export function decodeNavTree<T extends Record<string, any>>(nodes: T[]): T[] {
  for (const node of nodes) {
    const n = node as any;
    if (typeof n.title === "string") n.title = decodeEntities(n.title);
    if (typeof n.description === "string")
      n.description = decodeEntities(n.description);
    const children = n.children;
    if (Array.isArray(children)) decodeNavTree(children);
  }
  return nodes;
}
